import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { extractApiError } from '../utils/apiError';
import '../styles/CustomerPage.css';
import { useToast } from '../context/UIContext';

export default function CustomerModal({ customer, onClose, onSave }) {
    const toast = useToast();
    const [formData, setFormData] = useState({ name: '', address: '', comment: '', contactPerson: '', vatId: '', email: '', phone: '' });

    useEffect(() => {
        if (customer) setFormData(customer);
    }, [customer]);

    const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

    const handleSubmit = () => {
        if (!formData.name.trim()) {
            toast('Bitte Firmenname eingeben.');
            return;
        }
        const payload = {
            name: formData.name,
            address: formData.address,
            comment: formData.comment,
            contact_person: formData.contactPerson,
            vat_id: formData.vatId || null,
            email: formData.email || null,
            phone: formData.phone || null,
        };
        const request = customer
            ? api.patch(`/Company/${customer.id}`, payload)
            : api.post('/Company', payload);
        request.then(() => onSave()).catch(err => { toast(extractApiError(err, 'Fehler beim Speichern.')); });
    };

    const isEdit = Boolean(customer);

    return (
        <div className="modal-backdrop">
            <div className="customer-modal">

                <div className="customer-modal-header">
                    <div className="customer-modal-avatar">{formData.name?.[0]?.toUpperCase() || (isEdit ? '?' : '+')}</div>
                    <div>
                        <h3 className="customer-modal-title">{isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
                        <p className="customer-modal-sub">{isEdit ? formData.name : 'Fügen Sie einen neuen Kunden hinzu'}</p>
                    </div>
                    <button className="customer-modal-close" onClick={onClose} aria-label="Schließen">
                        <svg viewBox="0 0 20 20" fill="none">
                            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="customer-modal-body">
                    <div className="cm-field">
                        <label className="cm-label">Firmenname</label>
                        <div className="cm-input-wrap">
                            <svg className="cm-input-icon" viewBox="0 0 20 20" fill="none">
                                <rect x="3" y="5" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M7 5V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <input
                                className="cm-input"
                                placeholder="z.B. Musterfirma GmbH"
                                value={formData.name}
                                onChange={set('name')}
                            />
                        </div>
                    </div>

                    <div className="cm-field">
                        <label className="cm-label">Adresse</label>
                        <div className="cm-input-wrap">
                            <svg className="cm-input-icon" viewBox="0 0 20 20" fill="none">
                                <path d="M10 2a6 6 0 0 1 6 6c0 4-6 10-6 10S4 12 4 8a6 6 0 0 1 6-6Z" stroke="currentColor" strokeWidth="1.5"/>
                                <circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                            <input
                                className="cm-input"
                                placeholder="Straße, PLZ, Stadt"
                                value={formData.address}
                                onChange={set('address')}
                            />
                        </div>
                    </div>

                    <div className="cm-field">
                        <label className="cm-label">Ansprechpartner</label>
                        <div className="cm-input-wrap">
                            <svg className="cm-input-icon" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <input
                                className="cm-input"
                                placeholder="Name des Ansprechpartners"
                                value={formData.contactPerson}
                                onChange={set('contactPerson')}
                            />
                        </div>
                    </div>

                    <div className="cm-field">
                        <label className="cm-label">USt-IdNr. <span className="cm-optional">optional</span></label>
                        <div className="cm-input-wrap">
                            <svg className="cm-input-icon" viewBox="0 0 20 20" fill="none">
                                <rect x="3" y="4" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M7 8h6M7 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <input
                                className="cm-input"
                                placeholder="z.B. DE123456789"
                                value={formData.vatId}
                                onChange={set('vatId')}
                            />
                        </div>
                    </div>

                    <div className="cm-field">
                        <label className="cm-label">E-Mail <span className="cm-optional">optional</span></label>
                        <div className="cm-input-wrap">
                            <svg className="cm-input-icon" viewBox="0 0 20 20" fill="none">
                                <rect x="2" y="5" width="16" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <input
                                className="cm-input"
                                type="email"
                                placeholder="kontakt@firma.de"
                                value={formData.email}
                                onChange={set('email')}
                            />
                        </div>
                    </div>

                    <div className="cm-field">
                        <label className="cm-label">Telefon <span className="cm-optional">optional</span></label>
                        <div className="cm-input-wrap">
                            <svg className="cm-input-icon" viewBox="0 0 20 20" fill="none">
                                <path d="M6 2h3l1.5 4-2 1.5a10 10 0 0 0 4 4L14 9.5 18 11v3a2 2 0 0 1-2 2C7.16 16 4 8.84 4 4a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                                className="cm-input"
                                type="tel"
                                placeholder="+49 123 456789"
                                value={formData.phone}
                                onChange={set('phone')}
                            />
                        </div>
                    </div>

                    <div className="cm-field">
                        <label className="cm-label">Kommentar <span className="cm-optional">optional</span></label>
                        <textarea
                            className="cm-input cm-textarea"
                            placeholder="Interne Notizen zum Kunden…"
                            value={formData.comment}
                            onChange={set('comment')}
                            rows={3}
                        />
                    </div>
                </div>

                <div className="customer-modal-footer">
                    <button className="button-normal" onClick={handleSubmit}>
                        {isEdit ? 'Änderungen speichern' : 'Kunde anlegen'}
                    </button>
                    <button className="button-danger" onClick={onClose}>Abbrechen</button>
                </div>

            </div>
        </div>
    );
}